import classNames from 'classnames'
import {Formik} from 'formik'
import {useCallback, useMemo, useRef, useState} from 'react'
import conditionTypes from '../../../data/conditionTypes.json'
import {SecondaryTrashIcon} from '../../../icons'
import {colors} from '../../../utils/colors'
import {extractOnlyValues} from '../../../utils/forms'
import regexPatterns from '../../../utils/regexPatterns'
import {addDynamicRulesetSchema} from '../../../utils/validation'
import {SimpleBlueButton} from '../../core/button'
import ButtonLoader from '../../core/button/ButtonLoader'
import CustomSelect from '../../core/customSelect'
import Input from '../../core/input'
import './index.scss'
import text from './text.json'

const conditionTypeOptions = Object.values(conditionTypes.json_key_value_check.values)

const RulesetForm = ({
    rulesetFormInitialValues,
    rulesetTypeOptions,
    setRulesetValues,
    closeModal,
    ...props
}) => {
    const [isButtonLoading, setIsButtonLoading] = useState(false)
    const [conditions, setConditions] = useState(() => initializeConditions(rulesetFormInitialValues,
        props.isCronJobRuleset, rulesetTypeOptions))
    const formikRef = useRef()

    const defaultCondition = useMemo(() => rulesetFormInitialValues.conditions.pairs[0], [rulesetFormInitialValues])

    const getConditionOperatorOptions = useCallback((typeValue) => {
        if (!props.isCronJobRuleset) return conditionTypeOptions

        if (typeValue === rulesetTypeOptions?.result?.value) {
            return conditionTypeOptions.filter(opt => opt.value === conditionTypes.json_key_value_check.values.equal.value)
        }

        if (typeValue === rulesetTypeOptions?.message?.value) {
            return conditionTypeOptions.filter(opt => [
                conditionTypes.json_key_value_check.values.contains.value,
                conditionTypes.json_key_value_check.values.not_contains.value
            ].includes(opt.value))
        }

        return conditionTypeOptions
    }, [props.isCronJobRuleset])

    const getConditionKeyOrValueOptions = useCallback(optionsObj => optionsObj ? Object.values(optionsObj) : [], [])

    const addRuleset = async(values) => {
        const updatedType = Object.values(rulesetTypeOptions).find(type => type.value === values.type?.value)
        const filteredConditions = values.conditions.pairs.filter(c => c.values && c.key.value && c.operator.value)

        setRulesetValues({
            ...values,
            type: updatedType,
            conditions: {...values.conditions, pairs: filteredConditions}
        })

        setIsButtonLoading(false)
        closeModal()
    }

    const updateConditions = useCallback((conditionType, setFieldValue, values, errors, setErrors) => {
        if (!conditionType) return

        const isResult = conditionType.value === rulesetTypeOptions?.result?.value
        const isMessage = conditionType.value === rulesetTypeOptions?.message?.value
        const defaultKeyValue = props?.isCronJobRuleset ?
            rulesetTypeOptions?.[conditionType.value] :
            conditionType?.condition?.options?.default

        const updatedKey = {
            ...defaultCondition.key,
            value: defaultKeyValue,
            options: props?.isCronJobRuleset ?
                [defaultKeyValue] :
                defaultCondition.key.options
        }

        const updatedValues = {
            ...defaultCondition.values,
            value: isResult ? defaultKeyValue : '',
            options: getConditionKeyOrValueOptions(conditionType?.condition?.options),
            FormItem: isResult ? CustomSelect : Input,
            type: isMessage ? 'text' : defaultCondition?.values?.type,
            form_type: isMessage ? 'input' : defaultCondition?.values?.form_type
        }

        const newConditionPair = {
            key: updatedKey,
            operator: {
                ...defaultCondition.operator,
                options: getConditionOperatorOptions(conditionType.value),
                value: getConditionOperatorOptions(conditionType.value)[0]
            },
            values: updatedValues
        }

        setConditions({...values.conditions, pairs: [newConditionPair]})
        setFieldValue('conditions', {...values.conditions, pairs: extractOnlyValues([newConditionPair])})
        if (errors?.conditions?.pairs) setErrors({...errors, conditions: {}})
    }, [defaultCondition, getConditionOperatorOptions])

    const handleRemoveConditions = useCallback((index, setFieldValue, values, errors, setErrors) => {
        const updatedPairs = conditions.pairs.filter((_, i) => i !== index)
        setConditions({...conditions, pairs: updatedPairs})
        setFieldValue('conditions', {...values.conditions, pairs: extractOnlyValues(updatedPairs)})

        if (errors?.conditions?.pairs) {
            setErrors({
                ...errors,
                conditions: {
                    pairs: errors.conditions.pairs.filter((_, i) => i !== index)
                }
            })
        }
    }, [conditions])

    const handleConditionChange = useCallback((index, formItem, value, e, setFieldValue, values) => {
        const updatedConditions = [...conditions.pairs]
        const newValue = value || e?.target?.value || null
        const typeValue = values?.type?.value?.value

        let operatorOptions = getConditionOperatorOptions(typeValue)
        if (props.isCronJobRuleset && regexPatterns.digitsOnly.test(newValue)) {
            operatorOptions = conditionTypeOptions
        }

        updatedConditions[index] = {
            ...updatedConditions[index],
            [formItem.name]: {
                ...updatedConditions[index][formItem.name],
                value: newValue
            },
            ...formItem.name !== 'operator' && {
                operator: {
                    ...conditions.pairs[0].operator,
                    options: operatorOptions
                }
            }
        }

        const shouldAddNew = index === updatedConditions.length - 1 && typeValue !== rulesetTypeOptions?.result?.value
        if (shouldAddNew) {
            updatedConditions.push({
                ...defaultCondition,
                key: {
                    ...defaultCondition.key,
                    options: props?.isCronJobRuleset ?
                        [rulesetTypeOptions?.[typeValue]] :
                        defaultCondition.key.options,
                    value: null
                },
                operator: {
                    ...defaultCondition.operator,
                    options: getConditionOperatorOptions(typeValue),
                    value: getConditionOperatorOptions(typeValue)[0]
                },
                values: {
                    ...defaultCondition.values,
                    value: '',
                    FormItem: typeValue === rulesetTypeOptions?.result?.value ? CustomSelect : Input,
                    form_type: typeValue === rulesetTypeOptions?.result?.value ? 'select' : 'input'
                }
            })
        }

        setConditions({...conditions, pairs: updatedConditions})
        setFieldValue('conditions', {...values.conditions, pairs: extractOnlyValues(updatedConditions)})
    }, [conditions, rulesetTypeOptions, defaultCondition, getConditionOperatorOptions])

    const initialValues = useMemo(() => ({
        ...extractOnlyValues(rulesetFormInitialValues),
        conditions: {
            pairs:  extractOnlyValues(rulesetFormInitialValues.conditions.pairs)
        }
    }), [rulesetFormInitialValues])

    return (
        <Formik
            enableReinitialize
            validateOnBlur={false}
            validateOnChange={false}
            innerRef={formikRef}
            validationSchema={() => addDynamicRulesetSchema(formikRef?.current?.values)}
            initialValues={initialValues}
            onSubmit={addRuleset}
        >
            {({values, handleSubmit, setFieldValue, errors, setErrors}) => (
                <form onSubmit={handleSubmit} className="rulesets-form-container form-container">
                    {Object.values(rulesetFormInitialValues).map((field) =>
                        field?.FormItem
                            ? renderFormItem(field, values, setFieldValue, errors, updateConditions, setErrors)
                            : conditions.pairs.map((condition, index) => (
                                <div key={index} className="child-form-items">
                                    {Object.entries(condition).map(([formItemKey, formItem]) =>
                                        renderChildFormItem({
                                            formItemKey,
                                            formItem,
                                            index,
                                            rulesetFormInitialValues,
                                            errors,
                                            handleRemoveConditions,
                                            handleConditionChange,
                                            setFieldValue,
                                            values,
                                            setErrors
                                        })
                                    )}
                                </div>
                            ))
                    )}
                    <div className={classNames('form-actions', {'has-cancel-button': props?.hasCancelButton})}>
                        <SimpleBlueButton type="submit" className="form-action-submit">
                            {isButtonLoading && <ButtonLoader />}
                            <span>{text.save}</span>
                        </SimpleBlueButton>
                    </div>
                </form>
            )}
        </Formik>
    )
}

const initializeConditions = (initialValues, isCronJobRuleset, rulesetTypeOptions) => ({
    ...initialValues.conditions,
    pairs: [
        {
            ...initialValues.conditions.pairs[0],
            operator: {
                ...initialValues.conditions.pairs[0].operator,
                options: initializeConditionOptions(initialValues.type.value.value, isCronJobRuleset, rulesetTypeOptions)
            }
        }
    ]
})

const initializeConditionOptions = (typeValue, isCronJobRuleset, rulesetTypeOptions) => {
    if (!isCronJobRuleset) return conditionTypeOptions

    if (typeValue === rulesetTypeOptions?.result?.value) {
        return conditionTypeOptions.filter(opt => opt.value === conditionTypes.json_key_value_check.values.equal.value)
    }

    if (typeValue === rulesetTypeOptions?.message?.value) {
        return conditionTypeOptions.filter(opt => [conditionTypes.json_key_value_check.values.contains.value,
            conditionTypes.json_key_value_check.values.not_contains.value].includes(opt.value))
    }

    return conditionTypeOptions
}

const renderFormItem = (field, values, setFieldValue, errors, updateConditions, setErrors) => {
    const {FormItem, name, options, placeholder, info_text: infoText, label, type, form_type: formType} = field
    if (!FormItem) return null

    return (
        <FormItem
            key={name}
            options={options}
            name={name}
            placeholder={placeholder}
            value={values[name]?.value}
            infoText={infoText}
            error={errors?.[name]?.value}
            label={label}
            checked={values[name].value}
            type={type || 'text'}
            onChange={(e, value) => {
                const updatedValue = formType === 'checkbox' ? {...values[name], value: !values[name].value}
                    : formType === 'select' ? {value: {value: value.value, label: value.label}, type: value.type}
                        : {...values[name], value: e.target.value || ''}

                setFieldValue(name, updatedValue)
                if (name === 'type') updateConditions(value, setFieldValue, values, errors, setErrors)
            }}
        />
    )
}

const renderChildFormItem = ({
    formItemKey,
    formItem,
    index,
    rulesetFormInitialValues,
    errors,
    handleRemoveConditions,
    handleConditionChange,
    setFieldValue,
    values,
    setErrors
}) => {
    const ChildFormItem = formItem.FormItem
    if (!ChildFormItem) return null

    return (
        <ChildFormItem
            key={formItemKey}
            inputIsDisabled={formItemKey === 'key' && rulesetFormInitialValues.type.value.isInputDisabled}
            options={formItem.options}
            name={formItem.name}
            error={errors?.conditions?.pairs?.[index]?.[formItem.name]?.value}
            placeholder={formItem.placeholder}
            isClearable
            value={formItem.value}
            infoText={index === 0 && formItem.info_text}
            label={formItem.label}
            type={formItem.type || 'text'}
            hasLabel={formItem.hasLabel}
            customIcon={
                index !== 0 && formItem?.hasCustomIcon && (
                    <SecondaryTrashIcon stroke={colors.colorRed} />
                )
            }
            customIconOnclick={() => handleRemoveConditions(index, setFieldValue, values, errors, setErrors)}
            onChange={(e, value) => handleConditionChange(index, formItem, value, e, setFieldValue, values)}
        />
    )
}

export default RulesetForm
